/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BulkCancelCommandV1 } from '../models/bulk-cancel-command-v-1';
import { BulkCancelResponseV1 } from '../models/bulk-cancel-response-v-1';
import { CursorPaginatedResponseApprovedRequestExportDtov1 } from '../models/cursor-paginated-response-approved-request-export-dtov-1';
import { CursorPaginationRequest } from '../models/cursor-pagination-request';
import { ExistingSupplierRequestCommandV1 } from '../models/existing-supplier-request-command-v-1';
import { FileUploadResponseV1 } from '../models/file-upload-response-v-1';
import { NewSupplierRequestCommandV1 } from '../models/new-supplier-request-command-v-1';
import { RequestErpSyncStatusIntegrationCommandV1 } from '../models/request-erp-sync-status-integration-command-v-1';
import { RequestIntegrationQueryV1 } from '../models/request-integration-query-v-1';
import { RequestPrepopulateCommandV1 } from '../models/request-prepopulate-command-v-1';
import { SupplierRequestQueryV1 } from '../models/supplier-request-query-v-1';


/**
 * API for managing supplier requests via external system integration (V1)
 */
@Injectable({
  providedIn: 'root',
})
export class V1RequestIntegrationService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation createRequestNewSupplier
   */
  static readonly CreateRequestNewSupplierPath = '/v1/integration/supplier-requests';

  /**
   * Create a supplier request for a new supplier.
   *
   * Creates a new supplier and associates it with a new request using the specified form.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRequestNewSupplier()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRequestNewSupplier$Response(params: {

    /**
     * New supplier request creation command
     */
    body: NewSupplierRequestCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequestIntegrationQueryV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.CreateRequestNewSupplierPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequestIntegrationQueryV1>;
      })
    );
  }

  /**
   * Create a supplier request for a new supplier.
   *
   * Creates a new supplier and associates it with a new request using the specified form.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRequestNewSupplier$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRequestNewSupplier(params: {

    /**
     * New supplier request creation command
     */
    body: NewSupplierRequestCommandV1
  },
  context?: HttpContext

): Observable<RequestIntegrationQueryV1> {

    return this.createRequestNewSupplier$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequestIntegrationQueryV1>) => r.body as RequestIntegrationQueryV1)
    );
  }

  /**
   * Path part for operation createRequestExistingSupplier
   */
  static readonly CreateRequestExistingSupplierPath = '/v1/integration/supplier-requests';

  /**
   * Create a supplier request for an existing supplier.
   *
   * Creates a new request for an existing supplier using the specified form.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRequestExistingSupplier()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRequestExistingSupplier$Response(params: {

    /**
     * Existing supplier request creation command
     */
    body: ExistingSupplierRequestCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<RequestIntegrationQueryV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.CreateRequestExistingSupplierPath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RequestIntegrationQueryV1>;
      })
    );
  }

  /**
   * Create a supplier request for an existing supplier.
   *
   * Creates a new request for an existing supplier using the specified form.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRequestExistingSupplier$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRequestExistingSupplier(params: {

    /**
     * Existing supplier request creation command
     */
    body: ExistingSupplierRequestCommandV1
  },
  context?: HttpContext

): Observable<RequestIntegrationQueryV1> {

    return this.createRequestExistingSupplier$Response(params,context).pipe(
      map((r: StrictHttpResponse<RequestIntegrationQueryV1>) => r.body as RequestIntegrationQueryV1)
    );
  }

  /**
   * Path part for operation uploadRequestQuestionFilesV1
   */
  static readonly UploadRequestQuestionFilesV1Path = '/v1/integration/supplier-requests/{requestId}/{questionId}/files';

  /**
   * Upload files for a supplier request scoped to a question.
   *
   * Uploads files to a question-scoped blob path (requestId/questionId/filename) and returns validated filenames, preserving the original characters (including non-Latin scripts), to reference in the prepopulate call. Same filename uploaded to the same question overwrites.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadRequestQuestionFilesV1()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadRequestQuestionFilesV1$Response(params: {
    requestId: string;
    questionId: string;
    body?: {

/**
 * Array of files to upload
 */
'files': Array<Blob>;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<FileUploadResponseV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.UploadRequestQuestionFilesV1Path, 'post');
    if (params) {
      rb.path('requestId', params.requestId, {});
      rb.path('questionId', params.questionId, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FileUploadResponseV1>;
      })
    );
  }

  /**
   * Upload files for a supplier request scoped to a question.
   *
   * Uploads files to a question-scoped blob path (requestId/questionId/filename) and returns validated filenames, preserving the original characters (including non-Latin scripts), to reference in the prepopulate call. Same filename uploaded to the same question overwrites.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadRequestQuestionFilesV1$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadRequestQuestionFilesV1(params: {
    requestId: string;
    questionId: string;
    body?: {

/**
 * Array of files to upload
 */
'files': Array<Blob>;
}
  },
  context?: HttpContext

): Observable<FileUploadResponseV1> {

    return this.uploadRequestQuestionFilesV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<FileUploadResponseV1>) => r.body as FileUploadResponseV1)
    );
  }

  /**
   * Path part for operation uploadRequestFilesV1
   */
  static readonly UploadRequestFilesV1Path = '/v1/integration/supplier-requests/{requestId}/files';

  /**
   * Upload files for a supplier request.
   *
   * Deprecated - use the question-scoped upload endpoint instead. Uploads files to blob storage for the specified request. Returns list of validated filenames, preserving the original characters (including non-Latin scripts), to reference in the prepopulate call.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadRequestFilesV1()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   *
   * @deprecated
   */
  uploadRequestFilesV1$Response(params: {
    requestId: string;
    body?: {

/**
 * Array of files to upload
 */
'files': Array<Blob>;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<FileUploadResponseV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.UploadRequestFilesV1Path, 'post');
    if (params) {
      rb.path('requestId', params.requestId, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<FileUploadResponseV1>;
      })
    );
  }

  /**
   * Upload files for a supplier request.
   *
   * Deprecated - use the question-scoped upload endpoint instead. Uploads files to blob storage for the specified request. Returns list of validated filenames, preserving the original characters (including non-Latin scripts), to reference in the prepopulate call.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadRequestFilesV1$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   *
   * @deprecated
   */
  uploadRequestFilesV1(params: {
    requestId: string;
    body?: {

/**
 * Array of files to upload
 */
'files': Array<Blob>;
}
  },
  context?: HttpContext

): Observable<FileUploadResponseV1> {

    return this.uploadRequestFilesV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<FileUploadResponseV1>) => r.body as FileUploadResponseV1)
    );
  }

  /**
   * Path part for operation listApprovedRequestsV1
   */
  static readonly ListApprovedRequestsV1Path = '/v1/integration/supplier-requests/list';

  /**
   * List supplier requests for export using cursor-based pagination.
   *
   * Retrieves supplier requests for ERP consumption using continuation token pagination. Defaults to APPROVED status when no status filter is supplied, preserving existing behavior. Supports filtering by id, supplierId, formId, organizationsIds, createdDate, completedDate, and status. The status filter accepts one or more of: Approved, ERP synced, ERP sync failed - any other status value is rejected. Date filters accept ISO 8601 format or epoch milliseconds. Maximum page size is 200. Default sort is createdDate DESC.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listApprovedRequestsV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listApprovedRequestsV1$Response(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<CursorPaginatedResponseApprovedRequestExportDtov1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.ListApprovedRequestsV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<CursorPaginatedResponseApprovedRequestExportDtov1>;
      })
    );
  }

  /**
   * List supplier requests for export using cursor-based pagination.
   *
   * Retrieves supplier requests for ERP consumption using continuation token pagination. Defaults to APPROVED status when no status filter is supplied, preserving existing behavior. Supports filtering by id, supplierId, formId, organizationsIds, createdDate, completedDate, and status. The status filter accepts one or more of: Approved, ERP synced, ERP sync failed - any other status value is rejected. Date filters accept ISO 8601 format or epoch milliseconds. Maximum page size is 200. Default sort is createdDate DESC.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listApprovedRequestsV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  listApprovedRequestsV1(params: {

    /**
     * Cursor pagination, sorting, and filter parameters
     */
    body: CursorPaginationRequest
  },
  context?: HttpContext

): Observable<CursorPaginatedResponseApprovedRequestExportDtov1> {

    return this.listApprovedRequestsV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<CursorPaginatedResponseApprovedRequestExportDtov1>) => r.body as CursorPaginatedResponseApprovedRequestExportDtov1)
    );
  }

  /**
   * Path part for operation prepopulateRequest
   */
  static readonly PrepopulateRequestPath = '/v1/integration/supplier-requests/{requestId}';

  /**
   * Prepopulate an existing supplier request with data.
   *
   * Prepopulates internal, external, and/or notes data sections of an existing request in Draft status and optionally sets the target status. Allowed target statuses: Draft, Requires review (internal), Requires review (external). Request must be in Draft status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `prepopulateRequest()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  prepopulateRequest$Response(params: {
    requestId: string;

    /**
     * Prepopulate command with target status and section data
     */
    body: RequestPrepopulateCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierRequestQueryV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.PrepopulateRequestPath, 'patch');
    if (params) {
      rb.path('requestId', params.requestId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierRequestQueryV1>;
      })
    );
  }

  /**
   * Prepopulate an existing supplier request with data.
   *
   * Prepopulates internal, external, and/or notes data sections of an existing request in Draft status and optionally sets the target status. Allowed target statuses: Draft, Requires review (internal), Requires review (external). Request must be in Draft status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `prepopulateRequest$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  prepopulateRequest(params: {
    requestId: string;

    /**
     * Prepopulate command with target status and section data
     */
    body: RequestPrepopulateCommandV1
  },
  context?: HttpContext

): Observable<SupplierRequestQueryV1> {

    return this.prepopulateRequest$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierRequestQueryV1>) => r.body as SupplierRequestQueryV1)
    );
  }

  /**
   * Path part for operation updateRequestErpSyncStatusV1
   */
  static readonly UpdateRequestErpSyncStatusV1Path = '/v1/integration/supplier-requests/{requestId}/erp-sync-status';

  /**
   * Update the ERP sync status and/or reference of a supplier request.
   *
   * Updates the ERP sync status of a request, optionally together with the request ERP reference. From Approved, the status can be set to ERP synced or ERP sync failed. From ERP sync failed, it can only be set to ERP synced. ERP synced is terminal. Resending the current status is allowed and is treated as a reference-only update — e.g. a repeat sync failure with a new error message.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRequestErpSyncStatusV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRequestErpSyncStatusV1$Response(params: {
    requestId: string;

    /**
     * Target ERP sync status and optional request ERP reference
     */
    body: RequestErpSyncStatusIntegrationCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SupplierRequestQueryV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.UpdateRequestErpSyncStatusV1Path, 'patch');
    if (params) {
      rb.path('requestId', params.requestId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SupplierRequestQueryV1>;
      })
    );
  }

  /**
   * Update the ERP sync status and/or reference of a supplier request.
   *
   * Updates the ERP sync status of a request, optionally together with the request ERP reference. From Approved, the status can be set to ERP synced or ERP sync failed. From ERP sync failed, it can only be set to ERP synced. ERP synced is terminal. Resending the current status is allowed and is treated as a reference-only update — e.g. a repeat sync failure with a new error message.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRequestErpSyncStatusV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRequestErpSyncStatusV1(params: {
    requestId: string;

    /**
     * Target ERP sync status and optional request ERP reference
     */
    body: RequestErpSyncStatusIntegrationCommandV1
  },
  context?: HttpContext

): Observable<SupplierRequestQueryV1> {

    return this.updateRequestErpSyncStatusV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<SupplierRequestQueryV1>) => r.body as SupplierRequestQueryV1)
    );
  }

  /**
   * Path part for operation bulkCancelSupplierRequestsIntegration
   */
  static readonly BulkCancelSupplierRequestsIntegrationPath = '/v1/integration/supplier-requests/bulk/cancellation';

  /**
   * Cancel multiple supplier requests in bulk.
   *
   * Cancels multiple supplier requests with a shared reason. Returns per-request results.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkCancelSupplierRequestsIntegration()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkCancelSupplierRequestsIntegration$Response(params: {

    /**
     * Bulk cancellation details with request IDs and mandatory reason
     */
    body: BulkCancelCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<BulkCancelResponseV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.BulkCancelSupplierRequestsIntegrationPath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BulkCancelResponseV1>;
      })
    );
  }

  /**
   * Cancel multiple supplier requests in bulk.
   *
   * Cancels multiple supplier requests with a shared reason. Returns per-request results.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `bulkCancelSupplierRequestsIntegration$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkCancelSupplierRequestsIntegration(params: {

    /**
     * Bulk cancellation details with request IDs and mandatory reason
     */
    body: BulkCancelCommandV1
  },
  context?: HttpContext

): Observable<BulkCancelResponseV1> {

    return this.bulkCancelSupplierRequestsIntegration$Response(params,context).pipe(
      map((r: StrictHttpResponse<BulkCancelResponseV1>) => r.body as BulkCancelResponseV1)
    );
  }

  /**
   * Path part for operation getSupplierRequestFileV1
   */
  static readonly GetSupplierRequestFileV1Path = '/v1/integration/supplier-requests/{requestId}/files/{questionName}/{fileName}';

  /**
   * Retrieve a file attached to a supplier request.
   *
   * Redirects (302) to a short-lived signed URL for the requested file. questionName and fileName are the values referenced in the internalData/externalData/notesData file answers returned by the list endpoint. Only available for requests with status Approved, ERP synced, or ERP sync failed, consistent with the list endpoint.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSupplierRequestFileV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupplierRequestFileV1$Response(params: {
    requestId: string;
    questionName: string;
    fileName: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, V1RequestIntegrationService.GetSupplierRequestFileV1Path, 'get');
    if (params) {
      rb.path('requestId', params.requestId, {});
      rb.path('questionName', params.questionName, {});
      rb.path('fileName', params.fileName, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Retrieve a file attached to a supplier request.
   *
   * Redirects (302) to a short-lived signed URL for the requested file. questionName and fileName are the values referenced in the internalData/externalData/notesData file answers returned by the list endpoint. Only available for requests with status Approved, ERP synced, or ERP sync failed, consistent with the list endpoint.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSupplierRequestFileV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSupplierRequestFileV1(params: {
    requestId: string;
    questionName: string;
    fileName: string;
  },
  context?: HttpContext

): Observable<void> {

    return this.getSupplierRequestFileV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
